import { env } from '../../config';
import { useState, useEffect, useRef } from 'react';
import { Circles } from 'react-loader-spinner';
import { v4 as uuidv4 } from 'uuid';
import { HexColorInput } from "react-colorful";
import { PopoverPicker } from '../ColorPicker/PopoverPicker';
import {getSlideUploadUrl, uploadSlideToAws, createSlide, updateSlide, removeSlide, buildMessagingUrl, replaceUnderscore } from './utils';
import { getLocationsByProduct, getCommonLocationsBetweenUserPermsAndProduct } from '../../utils';
import constants from './constants.json';

import addImage from '../../images/icons/add-image.svg';
import imageIcon from '../../images/icons/image.svg';
import landscapeIcon from '../../images/icons/screens/landscape.svg';
import linkIcon from '../../images/icons/link.svg';
import mobileIcon from '../../images/icons/screens/mobile.svg';
import portraitIcon from '../../images/icons/screens/portrait-left.svg';
import textIcon from '../../images/icons/contentManagement/text-icon.svg';
import videoIcon from '../../images/icons/video.svg';

export default function EditSlide({ setEditStatus, customerId, slideToEdit, playlists, updatedBy, selectedLocationId, folderName, setRefreshKey, role, selectCallback, setIsOpenContentModal, permissions, setSlideData }) {
	const [uploadedImage, setUploadedImage] = useState({
		file: null,
		name: null,
		type: null,
		url: null
	});
	const [locations, setLocations] = useState([]);
    const [openSection, setOpenSection] = useState(slideToEdit?.type || 'image'); 
	const [slideName, setSlideName] = useState('');
	const [slidePlaylists, setSlidePlaylists] = useState([]);
	const [slide, setSlide] = useState({
		...slideToEdit, 
		locationTags: Array.from(
			new Set([...(slideToEdit?.locationTags || []), selectedLocationId])
		)
	});
	const [typeError, setTypeError] = useState(false);
	const [errorMessage, setErrorMessage] = useState('');
	const [loading, setLoading] = useState(false);
	const dropFile = useRef(null);
	const fileInput = useRef(null);
	const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
	const [confirmDeleteMessage, setConfirmDeleteMessage] = useState('');
	const [enabledButtons, setEnabledButtons] = useState(false);
	const [required, setRequired] = useState(false);
	const [slideType, setSlideType] = useState('image');
	const [externalUrl, setExternalUrl] = useState('');
	const [deviceTypes, setDeviceTypes] = useState(['landscape']);

	const [showConfirmLocationChangeModal, setShowConfirmLocationChangeModal] = useState(false);
	const [confirmLocationChangeMessage, setConfirmLocationChangeMessage] = useState('');

	useEffect(() => {
		if (slideToEdit?.url) {
			setSlideName(slideToEdit.label);
			setDeviceTypes(slideToEdit.deviceTypes || ['landscape', 'portrait', 'mobile']);
			if (slideToEdit.type === 'image') {
				var urlParts = slideToEdit.url.split('.');
				setUploadedImage({...uploadedImage, name: slideToEdit.label, url: slideToEdit.url, type: urlParts.pop()});
			}
			else if (slideToEdit?.type === 'messaging') {
				setSlideType('messaging');
			}
			else {
				setSlideType('link');
				setExternalUrl(slideToEdit.url);
			}

			// Determine what playlists the current slide belongs to
			if (playlists.length > 0) {
				var currentSlidePlaylists = [];
				playlists.forEach((playlist, index) => {
					playlist.playlistSlides.forEach(pSlide => {
						if (pSlide.slideId === slideToEdit.id) {
							currentSlidePlaylists.push(playlists[index]);
						}
					});
				});
				setSlidePlaylists(currentSlidePlaylists);
			}
		}
	}, [slideToEdit]);

	useEffect(() => {
		if (playlists.length > 0 && slideToEdit?.id) {
			const currentSlidePlaylists = playlists.filter(playlist =>
				playlist.playlistSlides.some(pSlide => pSlide.slideId === slideToEdit.id)
			);
			setSlidePlaylists(currentSlidePlaylists);
		}
	}, [playlists, slideToEdit?.id]);

	useEffect(() => {
		// Only look for specific user permissions if not an admin
		getLocationsByProduct(customerId, role === 'af_admin' ? null : updatedBy, 'contentLibrary')
		.then(locationsData => {
			setLocations(locationsData);
		});
	}, []);

	const handleSectionClick = (section) => {
		if (openSection === section) {
			setOpenSection(''); // close if already open
		} else {
			setOpenSection(section);
			setSlideType(section);
		}
		setEnabledButtons(true);
	};

	const onUpload = async (upload) => {
		const file = upload[0];
		if (file !== undefined) {
			let validFileTypes = ['image/jpeg', 'image/jpg', 'image/gif', 'image/svg+xml', 'image/png', 'image/webp'];
			if (!validFileTypes.includes(file.type)) {
				setTypeError(true);
				return;
			}
			const objectUrl = URL.createObjectURL(file);
			setUploadedImage({
				file: file,
				name: file.name,
				type: file.type.split('/')[1],
				url: objectUrl
			});
			setTypeError(false);
			setEnabledButtons(true);
			setRequired(false);
		}
	};

	const handleClick = () => {
		fileInput.current.click();
	};
	
	const closeModal = () => {
		setSlideName('');
		setUploadedImage({
			file: null,
			name: null,
			type: null,
			url: null
		});
		setEditStatus(null);
		setSlideData(null);
	}

	const validateSave = (event) => {
		if (!slideName) {
			setRequired('slideLabel');
			return;
		}
		if (slideType === 'image' && !uploadedImage.type) {
			setRequired('slideImage');
			return;
		}
		if (slideType === 'messaging' && (!slide?.messagingConfigs?.text || slide?.messagingConfigs?.text === '')) {
			setRequired('slideMessage');
			return;
		}
		if (slideType === 'link' && externalUrl === '') {
			setRequired('externalUrl');
			return;
		}
		if (slideType === 'link' && !externalUrl.startsWith('https')) {
			setRequired('externalUrlHttps');
			return;
		}
		// No device types selected
		if (deviceTypes.length === 0) {
			setRequired('deviceTypes');
			return;
		}

		const commonLocations = getCommonLocationsBetweenUserPermsAndProduct(slide.locationTags,permissions, 'contentLibrary');

		// If user is adding or editing item in My, Shared, or Branding folder, save without warning.
		if (['my_folder', 'shared_folder', 'branding_folder'].includes(folderName)) {
			saveSlide();
		} else {
			if (slide.createdby === updatedBy) { // Slide owner
				if (slide.locationTags.length === 0) {
					setShowConfirmLocationChangeModal(true);
					setConfirmLocationChangeMessage('delete');
				} else {
					saveSlide();
				}
			} else {
				// User relies on commonLocations for access
				if (commonLocations.length > 0) {
					saveSlide();
				} else {
					// No common locations anymore. That's because one of 2 things:
					// - The slide itself doesn't have location tags, therefore delete slide; OR
					// - User doesn't have perms for remaining location tags.
					if (slide.locationTags.length === 0) {
						setShowConfirmLocationChangeModal(true);
						setConfirmLocationChangeMessage('delete');
					} else {
						setShowConfirmLocationChangeModal(true);
						setConfirmLocationChangeMessage('lose_access');
					}
				}
			}
		}
	}

	const saveSlide = async () => {
		// Show loading screen while processing
		setLoading(true);

		let s3Url = '';
		const miscTags = folderName === 'shared_folder' ? ['shared'] : folderName === 'branding_folder' ? ['branding'] : [];

		// Upload to AWS if a new image was uploaded, regardless
		// of whether we're creating or editing a slide.
		if (slideType === 'image' && uploadedImage.url !== slide?.url) {
			// Eg. uploadedImage.file.type = image/jpeg
			const fileType = uploadedImage.file.type.split('/')[0];
			const fileFormat = uploadedImage.file.type.split('/')[1];

			const slideKey = uuidv4();
			await getSlideUploadUrl(customerId, slideKey, fileType, fileFormat)
			.then(async resp => {
				await uploadSlideToAws(resp, uploadedImage.file, uploadedImage.file.type)
				.then(async response => {
					//Successful upload to s3
					if (response.status === 200) {
						s3Url = `${env.AWS_URL}/${customerId}/${slideKey}.${fileFormat}`;
						if (s3Url.includes('.svg+xml')) {
							s3Url = s3Url.replace('svg+xml', encodeURIComponent('svg+xml'));
						}
					} else {
						setErrorMessage('There were issues uploading to S3. Status code: ' + response.status);
						setLoading(false);
						return;
					}
				})
			});
		}

		// New slide
		let updatedSlide;
		if (!slide?.id) {
			const slideUrl = slideType === 'image' ? s3Url : slideType === 'messaging' ? buildMessagingUrl(slide?.messagingConfigs) : externalUrl;

			updatedSlide = await createSlide({
				createdBy: updatedBy,
				customerId,
				label: slideName,
				messagingConfigs: slide?.messagingConfigs || {},
				url: slideUrl,
				type: slideType,
				locationTags: slide?.locationTags || [],
				miscTags: miscTags,
				deviceTypes,
				updatedBy
			});
		} else if (slide) {
			// Update the slide name
			if (slideName !== slide.label) {
				slide.label = slideName;
			}

			// Update the image.
			// There is a new image in S3, so delete the old one.
			if (slideType === 'image' && s3Url !== '') {
				slide.urlToDelete = slide.url;
				slide.url = s3Url;
			}
			// Update messaging link
			if (slideType === 'messaging') {
				const messagingUrl = buildMessagingUrl(slide?.messagingConfigs);
				if (messagingUrl !== slide.url) {
					slide.url = messagingUrl;
				}
			}
			// Update the link
			if (slideType === 'link' && externalUrl !== slide.url) {
				// If switching from image type to link type, delete the S3 url.
				if (slide.url.includes(env.S3_MESSAGING_BUCKET)) {
					slide.urlToDelete = slide.url;
				}
				slide.url = externalUrl;
			}

			slide.updatedBy = updatedBy;
			await updateSlide(slide.id, {...slide, type: slideType, deviceTypes: deviceTypes, miscTags: miscTags })
			.then(async response => {
				if (response.status === 200) {
					console.log('Successfully updated slide');
				} else {
					setErrorMessage('There were issues updating the slide. Status code: ' + response.status);
					setLoading(false);
					return;
				}
			})
		}

		// Show content again
		setLoading(false);
		setEditStatus(null);
		setRefreshKey(prev => prev + 1);
		
		if (typeof selectCallback === 'function') {
            selectCallback(slide.url ? slide: updatedSlide);
        }
	}

	const updateSlideName = (e) => {
		if (e.target.value.length > 0) {
			setRequired(false);
		}
		setSlideName(e.target.value);
		setEnabledButtons(true);
	}

	const getUploadTemplate = () => {
		return <div className="upload-template">
			<img className="icon" src={addImage} />
		</div>
	}

	const confirmDeleteSlide = async () => {
		var message = '';
		if (slidePlaylists.length > 0) {
			message = 'This item is currently used in one or more playlists. Deleting it will remove it from all of them. Are you sure you want to continue?';
		} else {
			message = 'This item is not used in any playlists. Are you sure you want to continue?';
		}

		setConfirmDeleteMessage(message);
		setShowConfirmDeleteModal(true);
	}

	const deleteSlide = async () => {
		setUploadedImage({
			file: null,
			name: null,
			type: null,
			url: null
		});
		// Delete slide from Slides and PlaylistsSlides tables
		await removeSlide(slide.id)
		.then(data => {
			setSlideName('');
			setEditStatus(null);
			setRefreshKey(prev => prev + 1);
		})
	}

	const updateSlideType = (type) => {
		setSlideType(type);
		setEnabledButtons(true);
	}

	const handleColorChange = (property, value) => {
        //Making sure value does not come in with a # at the start
        const color = value.length > 6 ? value.slice(1) : value;
		
        setSlide({
            ...slide,
            messagingConfigs: {
				...slide?.messagingConfigs,
				[property]: color
			}
        });
		setEnabledButtons(true);
    }

	const handleDeviceTypeChange = (e) => {
		const value = e.target.value;
		setDeviceTypes((prev) =>
			prev.includes(value)
				? prev.filter((v) => v !== value)
				: [...prev, value]
		);
		setEnabledButtons(true);
	};

	return <div className="EditSlide">
			{
				loading
				? <div className="Loading">
					<Circles
						height="80"
						width="80"
						color="#2386EE"
						ariaLabel="circles-loading"
						visible={true}
					/>
				</div>
				: showConfirmDeleteModal 
				? <ConfirmModal message={confirmDeleteMessage} setShowConfirmDeleteModal={setShowConfirmDeleteModal} deleteSlide={deleteSlide} />  
				: showConfirmLocationChangeModal
				? <ConfirmLocationChangeModal message={confirmLocationChangeMessage} setShowConfirmLocationChangeModal={setShowConfirmLocationChangeModal} saveSlide={saveSlide} deleteSlide={deleteSlide} />
				: <>
					<h1>{slideToEdit?.id ? 'Edit' : 'Add'} content item</h1>
					<div className="choices" style={{ display: 'flex', flexDirection: 'column' }}>
                        {/* Image Section */}
                        <div className={`choice pure-g collapsible-section ${slideType === 'image' ? 'open' : ''}`}>
							<div
								className="collapsible-header"
								style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
								onClick={() => handleSectionClick('image')}
							>
								<input type="radio" className="selected-header" checked={slideType === 'image'} onChange={() => {}} />
								<div className="img-icon pure-u-1-24">
									<img src={imageIcon} alt="Image icon" />
								</div>
								<div className="label pure-u-20-24">
									<p className="bold-text">Image</p>
								</div>
								<div style={{ marginLeft: 'auto', transition: 'transform 0.2s', display: 'flex', alignItems: 'center' }}>
									<svg
										width="20"
										height="20"
										viewBox="0 0 20 20"
										style={{ transform: openSection === 'image' ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 0.2s' }}
									>
										<polyline points="6 8 10 12 14 8" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
									</svg>
								</div>
							</div>
                            {openSection === 'image' && (
                                <div className="pure-u-15-24 collapsible-content">
									<div className="slide-container pure-g">
                                        <div className={`add-slide-container ${required === "slideImage" ? 'required' : ''}`}>
                                            <div className={`add-slide ${uploadedImage?.url ? 'no-border' : ''}`} ref={dropFile}>
                                                {uploadedImage?.url
                                                    ? <div className="uploaded-image">
                                                        <img src={uploadedImage.url} alt="Uploaded" />
                                                    </div>
                                                    : getUploadTemplate()
                                                }
                                            </div>
                                        </div>
										<div className="label pure-u-7-24">
											<p className="bold-text">Supported file types:</p>
											<p className="regular-text">WebP, JPG, PNG, or GIF </p>
											<p className="bold-text">Recommended dimensions:</p>
											<p className="regular-text">Landscape: 3840px wide / 2160px tall</p>
											<p className="regular-text">Portrait: 2160px wide / 3840px tall</p>
										</div>
                                        <div className="button-container pure-u-8-24">
                                            <button
                                                className="templates-btn"
                                                type="button"
                                                onClick={() => window.open(
                                                    "https://docs.google.com/presentation/d/1kQGE2enbFAdzyWhbmL4SX3RXFJ3VYCff/edit?usp=sharing&ouid=104968099129841040016&rtpof=true&sd=true",
                                                    "_blank"
                                                )}
                                                title="Get templates"
                                            >
                                                Get templates
                                            </button>
											<button className="upload-btn" type="button" onClick={handleClick} title="Upload image">Upload</button>
                                            <input
                                                type="file"
                                                onChange={e => onUpload(e.target.files)}
                                                ref={fileInput}
                                                style={{ display: 'none' }}
                                        	/>
                                        </div>
                                    </div>
                                    {typeError
                                        ? <p className="error-text">Incorrect input type</p>
                                        : <p className="error-text" style={{ visibility: 'hidden' }}>Hidden</p>
                                    }
                                </div>
                            )}
                        </div>
                        {/* Messaging Section */}
                        <div className={`choice pure-g collapsible-section ${slideType === 'messaging' ? 'open' : ''}`}>
							<div
								className="collapsible-header"
								style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
								onClick={() => handleSectionClick('messaging')}
							>
								<input type="radio" className="selected-header" checked={slideType === 'messaging'} onChange={() => {}} />
								<div className="img-icon pure-u-1-24">
									<img src={textIcon} alt="Messaging icon" />
								</div>
								<div className="label pure-u-20-24">
									<p className="bold-text">Quick text</p>
								</div>
								<div style={{ marginLeft: 'auto', transition: 'transform 0.2s', display: 'flex', alignItems: 'center' }}>
									<svg
										width="20"
										height="20"
										viewBox="0 0 20 20"
										style={{ transform: openSection === 'messaging' ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 0.2s' }}
									>
										<polyline points="6 8 10 12 14 8" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
									</svg>
								</div>
							</div>
                            {openSection === 'messaging' && (
                                <div className="pure-u-20-24 collapsible-content">
                                    <div className="input-container">
                                        <input type="text" value={slide?.messagingConfigs?.text || ''} placeholder="Enter your text" className="content-input" onChange={e => setSlide({ ...slide, messagingConfigs: { ...slide?.messagingConfigs, text: e.target.value } }, setEnabledButtons(true))} disabled={openSection !== 'messaging'} />
                                    </div>
                                    <div className="input-container">
                                        <p className="bold-text pure-u-3-24">Background:</p>
                                        <HexColorInput
                                            className="color-input"
                                            color={slide?.messagingConfigs?.color ? slide.messagingConfigs['color'] : '#ffffff'}
                                            onChange={(colorVal) => handleColorChange('color', colorVal)}
                                            prefixed={true}
                                            alpha={true}
                                            disabled={openSection !== 'messaging'}
                                            style={{ marginBottom: '1.4em' }}
                                        />
                                        <PopoverPicker
                                            color={slide?.messagingConfigs?.color ? '#' + slide.messagingConfigs['color'] : '#ffffff'}
                                            onChange={(colorVal) => handleColorChange('color', colorVal)}
                                            disabled={openSection !== 'messaging'}
                                            alpha={true}
                                        />
                                    </div>
                                    <div className="input-container">
                                        <p className="bold-text pure-u-3-24">Text color:</p>
                                        <HexColorInput
                                            className="color-input"
                                            color={slide?.messagingConfigs?.textColor ? slide.messagingConfigs['textColor'] : '#000000'}
                                            onChange={(colorVal) => handleColorChange('textColor', colorVal)}
                                            prefixed={true}
                                            alpha={true}
                                            disabled={openSection !== 'messaging'}
                                        />
                                        <PopoverPicker
                                            color={slide?.messagingConfigs?.textColor ? '#' + slide.messagingConfigs['textColor'] : '#000000'}
                                            onChange={(colorVal) => handleColorChange('textColor', colorVal)}
                                            alpha={true}
                                            disabled={openSection !== 'messaging'}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
						{/* Link section */}
                        <div className={`choice pure-g collapsible-section ${slideType === 'link' ? 'open' : ''}`}>
							<div
								className="collapsible-header"
								style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
								onClick={() => handleSectionClick('link')}
							>
								<input type="radio" className="selected-header" checked={slideType === 'link'} onChange={() => {}} />
								<div className="img-icon pure-u-1-24">
									<img src={linkIcon} alt="Link icon" />
								</div>
								<div className="label pure-u-20-24">
									<p className="bold-text">Website:</p>
								</div>
								<div style={{ marginLeft: 'auto', transition: 'transform 0.2s', display: 'flex', alignItems: 'center' }}>
									<svg
										width="20"
										height="20"
										viewBox="0 0 20 20"
										style={{ transform: openSection === 'link' ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 0.2s' }}
									>
										<polyline points="6 8 10 12 14 8" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
									</svg>
								</div>
							</div>
                            {openSection === 'link' && (
                                <div className="pure-u-20-24 collapsible-content">
                                    <p className="regular-text">Show the contents of a 3rd party URL. Note: Some 3rd party websites will not allow their websites to be shown in this manner.</p>
                                    <div className="input-container">
                                        <p className="bold-text pure-u-3-24">Secure URL:</p>
                                        <input type="text" value={externalUrl} placeholder="https://" className={"content-input " + (required === "externalUrl" ? ' required' : '')} onChange={e => { setExternalUrl(e.target.value); setEnabledButtons(true); }} disabled={openSection !== 'link'} />
                                    </div>
                                    <div className="input-guideline">Secure URLs only (https://)</div>
                                </div>
                            )}
                        </div>
                        {/* Video Section (disabled) */}
						<div className="choice pure-g collapsible-section">
							<div
								className="collapsible-header"
								style={{ cursor: 'default', display: 'flex', alignItems: 'center', color:'#CCD2D3' }}
								title="Video support coming soon"
							>
								<input type="radio" className="selected-header" checked={slideType === 'video'} disabled onChange={() => {}} />
								<div className="img-icon pure-u-1-24">
									{/* You can use a placeholder icon or reuse imageIcon for now */}
									<img src={videoIcon} alt="Video icon" />
								</div>
								<div className="label pure-u-20-24">
									<p className="bold-text">Video (Coming soon)</p>
								</div>
								<div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
									<svg
										width="20"
										height="20"
										viewBox="0 0 20 20"
										style={{ transform: 'rotate(-90deg)', transition: 'transform 0.2s' }}
									>
										<polyline points="6 8 10 12 14 8" fill="none" stroke="#CCD2D3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
									</svg>
								</div>
							</div>
						</div>
                        {/* Description Section */}
						<div className="choice pure-g" style={{ display: 'flex', flexDirection: 'row', gap: '2em', marginTop: 'auto' }}>
							<div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
								<div className="label">
									<p className="bold-text" style={{marginBottom: 0}}>Description <span style={{color: '#FD4A5C'}}>(required)</span></p>
									<p className="regular-text">Add a brief description to help you find this item later in your content library.</p>
									<div className="input-container" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '0.5em' }}>
										<input className={"desc-input " + (required === "slideLabel" ? 'required' : '')} type="text" value={slideName} onChange={e => updateSlideName(e)} placeholder="Enter a short description" maxLength="120" />
									</div>
									<div className="input-guideline">
										Maximum 120 characters
									</div>
									<div className="error-message">{errorMessage}</div>
								</div>
								<div className="label">
									<p className="bold-text" style={{marginBottom: 0}}>Device types <span style={{color: '#FD4A5C'}}>(required)</span></p>
									<p className="regular-text">Select which devices this item can be shown on.</p>
										<div className="device-checkbox-group">
											<label style={{ display: 'flex', alignItems: 'center', gap: '0.5em' }}>
												<input
													type="checkbox"
													value="landscape"
													checked={deviceTypes.includes('landscape')}
													onChange={handleDeviceTypeChange}
												/>
												<img src={landscapeIcon} alt="Landscape icon" style={{ width: '1.2em', height: '1.2em' }} />
												Screens - Landscape (default)
											</label>
											<label style={{ display: 'flex', alignItems: 'center', gap: '0.5em' }}>
												<input
													type="checkbox"
													value="portrait"
													checked={deviceTypes.includes('portrait')}
													onChange={handleDeviceTypeChange}
												/>
												<img src={portraitIcon} alt="Portrait icon" style={{ width: '1.2em', height: '1.2em' }} />
												Screens - Portrait
											</label>
											<label style={{ display: 'flex', alignItems: 'center', gap: '0.5em' }}>
												<input
													type="checkbox"
													value="mobile"
													checked={deviceTypes.includes('mobile')}
													onChange={handleDeviceTypeChange}
												/>
												<img src={mobileIcon} alt="Mobile icon" style={{ width: '1.2em', height: '1.2em' }} />
												Mobile
											</label>
										</div>
								</div>
							</div>
							<div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '1.5em' }}>
								<div className="label">
									<p className="bold-text" style={{marginBottom: 0}}>Sharing</p>
									<p className="regular-text">This item is being added to "{constants.NAME_MAP[folderName] ?? replaceUnderscore(folderName, ' ')}". To share it in other folders, select them from the list below. Anyone with access to those folders will be able to view it.</p>
									<div className="sharing-checkbox-group">
										{locations && Object.entries(locations).map(([mainKey, subLocations]) => {
											const allSubIds = subLocations.map(loc => loc.id);
											const mainChecked = allSubIds.length > 0 && allSubIds.every(id => (slide?.locationTags || []).includes(id));
											return (
												<div key={mainKey} className="sharing-main-group">
													<label className="main-checkbox">
														<input
															type="checkbox"
															value={mainKey}
															checked={mainChecked}
															disabled={allSubIds.length === 1 && allSubIds[0] === selectedLocationId} // Disable if the only sub-location is the selected location
															onChange={() => {
																setSlide(prev => {
																	const tags = prev?.locationTags || [];
																	let newTags;
																	if (mainChecked) {
																		// Remove all sub ids
																		newTags = tags.filter(t => !allSubIds.includes(t));
																	} else {
																		// Add all sub ids (avoid duplicates)
																		newTags = Array.from(new Set([...tags, ...allSubIds]));
																	}
																	return { ...prev, locationTags: newTags };
																});
																setEnabledButtons(true);
															}}
														/>
														{replaceUnderscore(mainKey, ', ')}
													</label>
													{Array.isArray(subLocations) && subLocations.length > 0 && (
														<div className="sharing-sub-group">
															{subLocations.map((subLoc, j) => {
																const subChecked = (slide?.locationTags || []).includes(subLoc.id);
																return (
																	<label key={subLoc.name + subLoc.id} className="sub-checkbox">
																		<input
																			type="checkbox"
																			value={subLoc.id}
																			checked={subChecked}
																			disabled={subLoc.id === selectedLocationId}
																			onChange={() => {
																				setSlide(prev => {
																					const tags = prev?.locationTags || [];
																					const exists = tags.includes(subLoc.id);
																					return {
																						...prev,
																						locationTags: exists
																							? tags.filter(t => t !== subLoc.id)
																							: [...tags, subLoc.id]
																					};
																				});
																				setEnabledButtons(true);
																			}}
																		/>
																		{subLoc.name}
																	</label>
																);
															})}
														</div>
													)}
												</div>
											);
										})}
									</div>
								</div>
							</div>
						</div>
                    </div>
					<div className="required-container">
						{
							required === 'deviceTypes' ? <p className="error-text">At least one device type must be selected</p> :
							required === 'slideLabel' ? <p className="error-text">Description is required</p> :
							required === 'slideImage' ? <p className="error-text">An image must be uploaded</p> :
							required === 'slideMessage' ? <p className="error-text">Message text is required</p> :
							required === 'externalUrl' ? <p className="error-text">A secure URL is required</p> :
							required === 'externalUrlHttps' ? <p className="error-text">URL must begin with https://</p> :
							<p className="error-text" style={{ visibility: 'hidden' }}>Hidden</p>
						}
					</div>
                    <div className="buttons-container">
                        <button className={`${slideToEdit?.id ? 'delete-button' : 'disabled-button'}`} onClick={confirmDeleteSlide} disabled={!slideToEdit?.id} title="Delete slide">Delete</button>
                        <div>
                            <button className="secondary-button" onClick={closeModal} title="Cancel changes">Cancel</button>
                            <button
                                disabled={!enabledButtons}
                                className={`${enabledButtons ? 'primary-button' : 'disabled-button'}`}
                                onClick={() => validateSave()}
                                title="Save changes"
                            >
                                Save
                            </button>
                        </div>
                    </div>
				</>
			}
	</div>
};

const ConfirmModal = ({message, setShowConfirmDeleteModal, deleteSlide}) => {
	return <div className="ConfirmModal">
		<div className="delete-slide-modal">
				<div>
					{message}
				</div>
				<div className="buttons-container">
					<button className="secondary-button" onClick={() => setShowConfirmDeleteModal(false)}>Cancel</button>
					<button className="primary-button" onClick={() => deleteSlide()}>Delete</button>
				</div>
		</div>
	</div>
}

const ConfirmLocationChangeModal = ({message, setShowConfirmLocationChangeModal, saveSlide, deleteSlide}) => {
	return <div className="ConfirmModal">
		<div className="delete-slide-modal">
				<div>
				{
					message === 'lose_access'
					? 'This content item is not shared with any location you can access. If you proceed, you will lose access until someone with access shares it to one of your locations. Do you wish to continue?'
					: 'This content item is not shared with any location within your organization. If you proceed, it will be deleted. This action cannot be undone. Do you wish to continue?'
				}
				</div>
				<div className="buttons-container">
					<button className="secondary-button" onClick={() => setShowConfirmLocationChangeModal(false)}>Cancel</button>
					<button
						className={message === 'lose_access' ? `primary-button` : 'delete-button-red-bg'}
						onClick={() => message === 'lose_access' ? saveSlide() : deleteSlide()}>
						{message === 'lose_access' ? 'OK' : 'Delete'}
					</button>
				</div>
		</div>
	</div>
}
